import type { Theme, ThemeOptions } from "@mui/material";
import { createTheme, darken, lighten, responsiveFontSizes } from "@mui/material";
import { deepmerge } from "@mui/utils";

import {
  backdrop,
  brandGray,
  brandPrimary,
  brandSecondary,
  darkGray,
  dirtyInputLight,
  error,
  lighterGray,
  lightGray,
  errorDarkTheme,
  warning,
  white,
} from "@waterly/utils-isomorphic";

export const mainTheme: ThemeOptions = {
  palette: {
    mode: "light" as const,
    primary: {
      main: brandPrimary,
    },
    secondary: {
      main: brandSecondary,
      contrastText: white,
    },
    background: {
      default: backdrop,
    },
    inputBackgroundDirty: {
      main: dirtyInputLight,
    },
    warning: {
      main: warning,
    },
    error: {
      main: error,
    },
    action: {
      disabledBackground: "rgba(0, 0, 0, 0.20)",
    },
    neutral: {
      main: lightGray,
      light: lighterGray,
      dark: darkGray,
      contrastText: white,
    },
  },
  typography: {
    h1: {
      fontSize: "1.5rem",
      fontWeight: 450,
      lineHeight: 1.5,
      letterSpacing: "0.02em",
    },
    h2: {
      fontSize: "1.4rem",
      fontWeight: 425,
      lineHeight: 1.5,
      letterSpacing: "0.009em",
    },
    h3: {
      fontSize: "1.25rem",
      fontWeight: 425,
      lineHeight: 1.5,
      letterSpacing: "0.001em",
    },
    h4: {
      fontSize: "1.2rem",
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: "0.0000em",
    },
    h5: {
      fontSize: "1.1rem",
      fontWeight: 300,
      lineHeight: 1.5,
    },
  },
  transitions: {
    duration: {
      shortest: 150,
      shorter: 100,
      short: 150,
      standard: 100,
      complex: 175,
      enteringScreen: 125,
      leavingScreen: 195,
    },
  },
  components: {
    MuiAlert: {
      variants: [
        {
          // this one is not in use yet
          props: { variant: "standard", color: "brand" },
          style: () => ({
            borderColor: brandPrimary,
            color: brandPrimary,
            backgroundColor: backdrop,
          }),
        },
        {
          props: { variant: "standard", color: "critical" },
          style: ({ theme }) => ({
            borderColor: theme.palette.error.main,
            color: theme.palette.error.main,
            backgroundColor: lighten(theme.palette.error.main, 0.9),
          }),
        },
        {
          props: { variant: "standard", color: "neutral" },
          style: ({ theme }) => ({
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            backgroundColor: white,
          }),
        },
      ],
    },
    MuiCssBaseline: {
      styleOverrides: {
        "@global": {
          html: {
            WebkitFontSmoothing: "auto",
          },
          ".MuiAutocomplete-popper": {
            touchAction: "manipulation",
            WebkitOverflowScrolling: "touch",
          },
          ".MuiAutocomplete-listbox": {
            touchAction: "manipulation",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
          },
          "::-webkit-scrollbar": {
            WebkitAppearance: "none",
            width: "10px",
            backgroundColor: "rgba(0, 0, 0, .1)",
            boxShadow: "0 0 3px inset rgba(0, 0, 0, .1)",
            borderRadius: "4px",
          },

          "::-webkit-scrollbar-thumb": {
            borderRadius: "4px",
            backgroundColor: "rgba(0, 0, 0, .5)",
            boxShadow: "0 0 1px inset rgba(255, 255, 255, .9)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          transition: "none !important",
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        input: {
          // height: 18,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: white,
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        current: {
          color: brandSecondary,
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          fontSize: ".8rem",
          lineHeight: ".8rem",
          maxWidth: "30%",
          textAlign: "end" as const,
          whiteSpace: "normal" as const,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& div[data-lastpass-icon-root="true"]': {
            display: "none",
          },
        },
      },
      defaultProps: {
        variant: "outlined" as const,
        margin: "dense" as const,
        size: "small" as const,
        autoComplete: "off",
        slotProps: {
          inputLabel: {
            shrink: true,
          },
        },
      },
    },
    MuiLinearProgress: {
      variants: [
        {
          props: { color: "disabled" },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.action.disabledBackground,
            "& .MuiLinearProgress-bar": {
              backgroundColor: theme.palette.action.disabled,
            },
          }),
        },
      ],
    },
    MuiBreadcrumbs: {
      defaultProps: {
        separator: ">",
      },
    },
  },
};

export const darkTheme = {
  ...mainTheme,
  palette: {
    ...(mainTheme.palette ?? {}),
    mode: "dark" as const,
    primary: {
      main: brandGray,
    },
    secondary: {
      main: brandSecondary,
    },
    background: {
      paper: darkGray,
      paper2: darken(darkGray, 0.1),
    },
    text: {
      primary: white,
    },
    error: {
      main: errorDarkTheme,
    },
  },
  components: {
    ...mainTheme.components,
    MuiOutlinedInput: {
      styleOverrides: {
        adornedEnd: {
          paddingRight: 0,
        },
        root: {
          backgroundColor: "inherit",
        },
      },
    },
  },
} as const;

export interface CreateMainThemeOptions {
  useDarkTheme?: boolean;
}

export const createMainTheme = ({ useDarkTheme = false }: CreateMainThemeOptions = {}): Theme => {
  return responsiveFontSizes(createTheme(useDarkTheme ? darkTheme : mainTheme));
};

export type WaterlyThemeMode = "light" | "dark";

export interface CreateWaterlyThemeOptions {
  colorMode?: WaterlyThemeMode;
  themeOverrides?: ThemeOptions;
}

export const createWaterlyTheme = ({
  colorMode = "light",
  themeOverrides,
}: CreateWaterlyThemeOptions = {}): Theme => {
  const baseThemeOptions = colorMode === "dark" ? darkTheme : mainTheme;
  const mergedThemeOptions = themeOverrides ? deepmerge(baseThemeOptions, themeOverrides) : baseThemeOptions;
  return responsiveFontSizes(createTheme(mergedThemeOptions));
};
